import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  ConfirmPopup,
  CustomSpinner,
  FormattedDurationText,
  InputJob,
  Row,
  Text,
} from '~/commonComponents';
import { UserProfession } from '~/enums/UserProfession';
import {
  getShouldExpandProfessionsJobPosting,
  getShouldOfficeShowEfda,
  getShouldUseParentPostShiftForChild,
} from '~/growthbook';
import { useIsParentOfficeSelector } from '~/hooks/useIsParentOfficeSelector';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { Colors } from '~/themes/colors';
import { getAllProfessionTypeOptions, getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { EducationUtils } from '~/utils/EducationUtils';
import briefcaseIcon from '../../../../images/briefcase.svg';
import calendarIcon from '../../../../images/calendar-icon.svg';
import MailIcon from '../../../../images/mail-solid.svg';
import ToothIcon from '../../../../images/tooth-icon.svg';
import userIcon from '../../../../images/user-icon.svg';
import Tooltip from '../../../Tooltip';
import { workerClassificationTooltipDescription } from '../../constants';
import { ProcedureCollapsibleSelection } from '../../temp/ProcedureCollapsibleSelection';
import { Title } from './CollapsibleRow/Title';
import { Value } from './CollapsibleRow/Value';
import { AssistedHygieneAvailableRow } from './Rows/AssistedHygieneAvailableRow';
import { AutoConfirmRow } from './Rows/AutoConfirmRow';
import { BreakRow } from './Rows/BreakRow';
import { DateRow } from './Rows/DateRow/DateRow';
import { SingleDateRow } from './Rows/DateRow/SingleDateRow';
import { HourlyRateRow } from './Rows/HourlyRateRow';
import { InviteRow } from './Rows/InviteRow';
import { OfficeRow } from './Rows/OfficeRow';
import { ProfessionalRow } from './Rows/ProfessionalRow';
import { SelectOfficeRow } from './Rows/SelectOfficeRow';
import { SkillsRow } from './Rows/SkillsRow';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { useGetJobPostDetail } from '~/domain/PermanentJobs';
import { Link } from 'react-router';

export const FORM_FIELDS = {
  DATES: 'dates',
  PROFESSIONAL: 'professional',
  SKILLS: 'skills',
  IS_ASSISTED_HYGIENE: 'isAssistedHygiene',
  PROCEDURES: 'procedures',
  HOURS: 'hours',
  BREAK: 'break',
  RATE: 'rate',
  AUTO_CONFIRM: 'autoConfirm',
  INVITE: 'invite',
};

export default function Form({
  job,
  formValues,
  setFormValues,
  defaultOpenCardIndex = null,
  disabledFields = [],
  editDate = false,
  source = 'post_shift',
}) {
  const user = useSelector((state) => state.user.selectedChildOffice);
  const parentUser = useSelector((state) => state.user);

  const timeLimitInMinutes = RemoteConfigUtils.getOnsiteInterviewThresholdInMinutes();

  const {
    officeId,
    lunch,
    professionType,
    skills,
    enableAutoFill,
    enableAutoFillFavorites,
    rate,
    isLunchPaid,
    offerEndTime,
    procedures,
    offerStartTime,
    isAssistedHygiene,
    professionals,
  } = formValues;

  // original profession type code, including sub-profession
  const professionTypeCode = UserProfession.getCode(professionType);

  const allProfessions = useSelector((state) => state.user.allProfessions);
  const showEfda = getShouldOfficeShowEfda();
  const allProfessionOptions = getAllProfessionTypeOptions(allProfessions, { showEfda });

  const userEducation = useSelector((state) => state.user.userEducation);

  const normalizedProfessionCode = UserProfession.getNormalizedCode(professionTypeCode);
  const specialtiesAndProcedures =
    userEducation?.[normalizedProfessionCode]?.specialtiesAndProcedures;

  const averageRating = useSelector((state) => state.job.averageRating) || {
    average: 49,
    max: 100,
    min: 18,
  };

  const availableProcedures = useMemo(
    () =>
      EducationUtils.getProceduresOfProfession({
        specialtiesAndProcedures,
        profession: UserProfession.getNormalizedCode(professionTypeCode),
        specialties: [skills],
      }),
    [skills, professionTypeCode, specialtiesAndProcedures],
  );

  const [selectedProcedureIndices, setSelectedProcedureIndices] = useState([]);

  const profRate = averageRating[professionTypeCode];

  const [openCardIndex, setOpenCardIndex] = useState(null);
  const rateRef = useRef(null);

  const handleCardToggle = (index) => {
    setOpenCardIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();

  const isFieldDisabled = (field) => disabledFields.includes(field);
  const isProfessionalEditable = !disabledFields.includes(FORM_FIELDS.PROFESSIONAL);

  const selectedOffice = useSelector((state) =>
    state.user?.childOffices?.find((office) => office.id === officeId),
  );
  const isParentOffice = useIsParentOfficeSelector();

  const showSelectOfficeRow =
    getShouldUseParentPostShiftForChild() && !job && !editDate && isParentOffice;

  const [
    confirmationOfficeChangeIfInviteIsSetModal,
    setConfirmationOfficeChangeIfInviteIsSetModal,
  ] = useState({
    show: false,
    officeId: '',
  });

  const { data, isLoading: isGetJobPostDetailLoading } = useGetJobPostDetail(
    job?.isOnsiteInterview ? job?.postingId : '',
  );

  useEffect(() => {
    if (defaultOpenCardIndex === FORM_FIELDS.RATE) {
      rateRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    setOpenCardIndex(defaultOpenCardIndex);
  }, [defaultOpenCardIndex]);

  // updates selected procedures when available procedures are available
  // or not empty
  //
  // this, to handle `userEducation` might still load when editing occurs
  useEffect(() => {
    if (
      availableProcedures &&
      availableProcedures.length > 0 &&
      procedures &&
      procedures.length > 0
    ) {
      const defaultProcedureIndices = procedures
        .map((procedure) =>
          availableProcedures.findIndex((currentProcedure) => procedure === currentProcedure),
        )
        .filter((procedureIndex) => procedureIndex > -1);

      setSelectedProcedureIndices(defaultProcedureIndices);
    }
  }, [availableProcedures, procedures]);

  useEffect(() => {
    if (isProfessionalEditable) {
      setSelectedProcedureIndices([]);
    }
  }, [isProfessionalEditable, professionType, skills]);

  const renderJobField = ({ title, value, icon }) => (
    <Row
      style={{
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1.5px solid ${Colors.neutral_100}`,
        borderRadius: 8,
        width: '60%',
        cursor: 'default',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 52px 20px 32px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img alt="icon" src={icon} style={{ width: 24, height: 24, marginRight: 8 }} />
          <Title bold fontSize={18} text={title} />
        </div>

        <Value text={value} />
      </div>
    </Row>
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          gap: 8,
        }}
      >
        {!showSelectOfficeRow ? (
          <OfficeRow
            value={
              selectedOffice?.office_name || job?.offer_owner?.office_name || user?.office_name
            }
          />
        ) : (
          <SelectOfficeRow
            value={officeId}
            onChange={(value) => {
              if (professionals?.length) {
                setConfirmationOfficeChangeIfInviteIsSetModal({
                  show: true,
                  officeId: value,
                });
              } else {
                setFormValues((prevState) => ({
                  ...prevState,
                  officeId: value,
                }));
              }
            }}
          />
        )}

        {job?.isOnsiteInterview &&
          renderJobField({
            title: 'Job Title:',
            value: isGetJobPostDetailLoading ? (
              <CustomSpinner size={12} />
            ) : (
              <>
                {data?.title ? (
                  <Link to={`/permanent-jobs/job-overview/${data?.id}`}>
                    <Text textDecoration="underline">{data?.title}</Text>
                  </Link>
                ) : (
                  '-'
                )}
              </>
            ),
            icon: briefcaseIcon,
          })}

        {job &&
          formValues.professional?.id &&
          renderJobField({
            title: 'Invite:',
            value: job?.professional?.name,
            icon: MailIcon,
          })}

        {parentUser.childOffices.length > 0 &&
          isWorkerClassificationEnabled &&
          user.workerClassification &&
          renderJobField({
            title: (
              <span
                style={{
                  marginRight: 16,
                }}
              >
                Worker Type:
                <span
                  style={{
                    marginLeft: '5px',
                    position: 'relative',
                    top: '5px',
                  }}
                >
                  <Tooltip content={workerClassificationTooltipDescription} />
                </span>
              </span>
            ),
            value:
              user.workerClassification.classificationType === 'contractor'
                ? 'Contracted'
                : 'Employed',
            icon: briefcaseIcon,
          })}

        {getShouldExpandProfessionsJobPosting() && (
          <>
            {isFieldDisabled(FORM_FIELDS.PROFESSIONAL) &&
              renderJobField({
                title: 'Professional Type:',
                value: !professionType ? 'Profession Type' : professionType,
                icon: userIcon,
              })}

            {!isFieldDisabled(FORM_FIELDS.PROFESSIONAL) && (
              <ProfessionalRow
                isOpen={openCardIndex === FORM_FIELDS.PROFESSIONAL}
                onToggle={() => handleCardToggle(FORM_FIELDS.PROFESSIONAL)}
                onConfirm={({ type }) => {
                  setFormValues((prevState) => ({
                    ...prevState,
                    professionType: type,
                    rate: 0,
                  }));
                  handleCardToggle(FORM_FIELDS.DATES);
                }}
                defaultSkill={skills}
                professionOptions={allProfessionOptions}
                defaultValue={professionTypeCode}
                professionals={professionals}
              />
            )}
          </>
        )}

        {editDate &&
          isFieldDisabled(FORM_FIELDS.DATES) &&
          renderJobField({
            title: 'Date:',
            value: moment(job?.localDate).format('ddd, MMM DD, YYYY'),
            icon: calendarIcon,
          })}

        {editDate && !isFieldDisabled(FORM_FIELDS.DATES) && (
          <SingleDateRow
            defaultValue={formValues?.localDate}
            isOpen={openCardIndex === FORM_FIELDS.DATES}
            onToggle={() => handleCardToggle(FORM_FIELDS.DATES)}
            onConfirm={(date) => {
              setFormValues((prevState) => ({
                ...prevState,
                localDate: date,
              }));
            }}
          />
        )}

        {!editDate && !isFieldDisabled(FORM_FIELDS.DATES) && (
          <DateRow
            isOpen={openCardIndex === FORM_FIELDS.DATES}
            onToggle={() => handleCardToggle(FORM_FIELDS.DATES)}
            onConfirm={() =>
              handleCardToggle(
                getShouldExpandProfessionsJobPosting()
                  ? FORM_FIELDS.SKILLS
                  : FORM_FIELDS.PROFESSIONAL,
              )
            }
          />
        )}

        {!getShouldExpandProfessionsJobPosting() && (
          <>
            {isFieldDisabled(FORM_FIELDS.PROFESSIONAL) &&
              renderJobField({
                title: 'Professional Type:',
                value: !professionType ? 'Profession Type' : professionType,
                icon: userIcon,
              })}

            {!isFieldDisabled(FORM_FIELDS.PROFESSIONAL) && (
              <ProfessionalRow
                isOpen={openCardIndex === FORM_FIELDS.PROFESSIONAL}
                onToggle={() => handleCardToggle(FORM_FIELDS.PROFESSIONAL)}
                onConfirm={({ type }) => {
                  setFormValues((prevState) => ({
                    ...prevState,
                    professionType: type,
                    rate: 0,
                  }));
                  handleCardToggle(FORM_FIELDS.PROFESSIONAL);
                }}
                defaultSkill={skills}
                professionOptions={allProfessionOptions}
                defaultValue={professionTypeCode}
                professionals={professionals}
              />
            )}
          </>
        )}

        {isFieldDisabled(FORM_FIELDS.SKILLS) &&
          renderJobField({
            title: 'Preferred Specialty:',
            value: job?.specialty?.split('_')?.[1] || 'General',
            icon: ToothIcon,
          })}

        {!isFieldDisabled(FORM_FIELDS.SKILLS) && (
          <SkillsRow
            isOpen={openCardIndex === FORM_FIELDS.SKILLS}
            onToggle={() => handleCardToggle(FORM_FIELDS.SKILLS)}
            onConfirm={({ selectedSkill }) => {
              setFormValues((prevState) => ({
                ...prevState,
                skills: selectedSkill,
              }));
            }}
            defaultSkill={skills}
            specialtiesAndProcedures={specialtiesAndProcedures}
          />
        )}

        {professionTypeCode === UserProfession.RDH && (
          <AssistedHygieneAvailableRow
            isOpen={openCardIndex === FORM_FIELDS.IS_ASSISTED_HYGIENE}
            onToggle={() => handleCardToggle(FORM_FIELDS.IS_ASSISTED_HYGIENE)}
            onConfirm={(value) => {
              setFormValues((prevState) => ({
                ...prevState,
                isAssistedHygiene: value,
              }));
            }}
            defaultValue={isAssistedHygiene}
          />
        )}

        {getIsAllowOfficeToPostProcedure(professionType, skills) && (
          <ProcedureCollapsibleSelection
            useRowStyle
            availableProcedures={availableProcedures}
            selectedProcedureIndices={selectedProcedureIndices}
            expanded={openCardIndex === FORM_FIELDS.PROCEDURES}
            onPressed={() => handleCardToggle(FORM_FIELDS.PROCEDURES)}
            onItemPressed={(index) => {
              const indices = (() => {
                const selected = selectedProcedureIndices.includes(index);
                const collection = selectedProcedureIndices;
                const maxSelection = 4;

                if (selected) {
                  return collection.filter((currentIndex) => currentIndex !== index);
                }

                if (!(maxSelection && collection.length >= maxSelection)) {
                  return [...collection, index];
                }

                return collection;
              })();

              setSelectedProcedureIndices(indices);
              setFormValues((prevState) => ({
                ...prevState,
                procedures: indices.map((index) => availableProcedures[index]),
              }));
            }}
          />
        )}

        <Box width="60%">
          <InputJob.Hours
            isOpen={openCardIndex === FORM_FIELDS.HOURS}
            timeLimitInMinutes={job?.isOnsiteInterview ? timeLimitInMinutes : undefined}
            guidanceText={
              job?.isOnsiteInterview ? (
                <Text color={Colors.neutral_500} italic>
                  Onsite interviews can run up to{' '}
                  <FormattedDurationText duration={timeLimitInMinutes} />, giving you the time you
                  need to
                  <br />
                  evaluate skills and overall fit.
                </Text>
              ) : undefined
            }
            valueArrival={offerStartTime}
            valueDeparture={offerEndTime}
            onToggle={() => handleCardToggle(FORM_FIELDS.HOURS)}
            onChange={(arrival, departure) => {
              setFormValues((prevState) => ({
                ...prevState,
                offerStartTime: arrival,
                offerEndTime: departure,
              }));
              handleCardToggle(FORM_FIELDS.BREAK);
            }}
          />
        </Box>

        <BreakRow
          isOpen={openCardIndex === FORM_FIELDS.BREAK}
          onToggle={() => handleCardToggle(FORM_FIELDS.BREAK)}
          onConfirm={({ time, isPaid }) => {
            setFormValues((prevState) => ({ ...prevState, lunch: time, isLunchPaid: isPaid }));
          }}
          defaultBreak={lunch}
          defaultIsPaid={isLunchPaid}
        />

        <HourlyRateRow
          ref={rateRef}
          isOpen={openCardIndex === FORM_FIELDS.RATE}
          onToggle={() => handleCardToggle(FORM_FIELDS.RATE)}
          onConfirm={(offerRate) => {
            setFormValues((prevState) => ({ ...prevState, rate: offerRate }));
            handleCardToggle(FORM_FIELDS.RATE);
          }}
          averagePrice={profRate}
          defaultValue={rate}
        />

        {!job && (
          <InviteRow
            onConfirm={(values) => {
              setFormValues((prevState) => ({
                ...prevState,
                professionals: values,
              }));
            }}
            defaultProfessionals={professionals}
            professionType={professionTypeCode}
            officeId={officeId || user?.id}
          />
        )}

        {!job?.isOnsiteInterview && (
          <AutoConfirmRow
            onConfirm={({ eligibleProfessionals, favorites }) => {
              setFormValues((prevState) => ({
                ...prevState,
                enableAutoFill: eligibleProfessionals,
                enableAutoFillFavorites: favorites,
              }));
            }}
            value={{
              eligibleProfessionals: enableAutoFill || false,
              favorites: enableAutoFillFavorites || false,
            }}
            professionType={professionType}
            skills={skills}
            source={source}
          />
        )}
      </div>

      {confirmationOfficeChangeIfInviteIsSetModal.show && (
        <ConfirmPopup
          title="Confirm Office Change"
          description="Changing the office will clear the professionals you’ve selected to invite for this shift. Do you want to continue?"
          descriptionStyle={{ marginBottom: 28 }}
          leftButtonText="Go Back"
          leftButtonAction={() => {
            setConfirmationOfficeChangeIfInviteIsSetModal({
              show: false,
              officeId: '',
            });
          }}
          rightButtonText="Continue"
          rightButtonAction={() => {
            setFormValues((prevState) => ({
              ...prevState,
              officeId: confirmationOfficeChangeIfInviteIsSetModal.officeId,
              professionals: undefined,
            }));
            setConfirmationOfficeChangeIfInviteIsSetModal({
              show: false,
              officeId: '',
            });
          }}
        />
      )}
    </>
  );
}
